import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Shield, ArrowLeft, Download, Mail, CheckCircle, 
  MapPin, Clock, ArrowRight, Printer, Copy, Check
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import AppFooter from '../components/AppFooter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BUREAU_INFO = {
  equifax: {
    name: 'Equifax Information Services LLC',
    address: 'P.O. Box 740256',
    city: 'Atlanta',
    state: 'GA',
    zip: '30374-0256',
    color: '#B76E79'
  },
  experian: {
    name: 'Experian',
    address: 'P.O. Box 4500',
    city: 'Allen',
    state: 'TX',
    zip: '75013',
    color: '#36454F'
  },
  transunion: {
    name: 'TransUnion LLC',
    address: 'Consumer Dispute Center, P.O. Box 2000',
    city: 'Chester',
    state: 'PA',
    zip: '19016',
    color: '#4CAF50'
  }
};

export default function MailHubPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [dispute, setDispute] = useState(null);
  const [marking, setMarking] = useState(false);
  const [mailed, setMailed] = useState(false);
  const [mailedData, setMailedData] = useState(null);

  useEffect(() => {
    if (location.state?.dispute) {
      setDispute(location.state.dispute);
    }
  }, [location.state]);

  const handleDownload = async () => {
    if (!dispute) return;
    const token = localStorage.getItem('selah_access_token');
    window.location.href = `${API_URL}/api/dispute/${dispute.dispute_id}/letter?token=${token}`;
  };

  const handleMarkMailed = async () => {
    if (!dispute) return;
    
    setMarking(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/dispute/${dispute.dispute_id}/mark-mailed`,
        {},
        { withCredentials: true }
      );
      setMailed(true);
      setMailedData(data);
    } catch (error) {
      console.error('Failed to mark as mailed:', error);
    } finally {
      setMarking(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const bureauInfo = BUREAU_INFO[dispute?.bureau?.toLowerCase()] || BUREAU_INFO.equifax;

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="mail-hub-page">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Shield className="w-8 h-8 text-[#B76E79]" strokeWidth={1.5} />
              <span className="ml-2 font-['Playfair_Display'] text-xl font-semibold text-[#36454F]">
                Selah Slate&trade;
              </span>
            </div>
            <Button 
              variant="ghost" 
              onClick={handleLogout}
              className="text-[#36454F]/70 hover:text-[#36454F] hover:bg-[#36454F]/5 font-['Manrope']"
            >
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Link */}
        <Link 
          to="/dashboard" 
          className="inline-flex items-center text-[#36454F]/65 hover:text-[#36454F] font-['Manrope'] mb-6 transition-colors"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
          Back to Dashboard
        </Link>

        {/* Success Banner */}
        <div className="mb-8 p-6 bg-[#4CAF50]/10 border border-[#4CAF50]/30 rounded-sm animate-fade-in-up">
          <div className="flex items-center">
            <CheckCircle className="w-8 h-8 text-[#4CAF50] mr-4" strokeWidth={1.5} />
            <div>
              <h1 className="font-['Playfair_Display'] text-2xl font-semibold text-[#36454F]">
                Your Dispute Letter is Ready!
              </h1>
              <p className="text-[#36454F]/65 font-['Manrope'] mt-1">
                Download, print, and mail your FCRA Section 611 compliant letter
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Download Section */}
          <Card className="border-[#36454F]/15 bg-white animate-fade-in-up animation-delay-100">
            <CardHeader>
              <CardTitle className="font-['Playfair_Display'] text-xl text-[#36454F] flex items-center">
                <Download className="w-5 h-5 mr-2 text-[#B76E79]" strokeWidth={1.5} />
                Download Letter
              </CardTitle>
              <CardDescription className="font-['Manrope'] text-[#36454F]/65">
                Your personalized dispute letter for {dispute?.bureau || 'the bureau'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-6 bg-[#F5F5F5] border border-[#36454F]/10 rounded-sm text-center">
                <Mail className="w-12 h-12 text-[#B76E79] mx-auto mb-3" strokeWidth={1.5} />
                <p className="font-['Manrope'] font-medium text-[#36454F] mb-1">
                  FCRA Section 611 Dispute Letter
                </p>
                <p className="text-sm text-[#36454F]/50 font-['Manrope']">
                  PDF Document • Ready to Print
                </p>
              </div>
              
              <Button
                onClick={handleDownload}
                className="w-full h-12 bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] font-medium rounded-sm transition-all duration-300"
                data-testid="download-letter-btn"
              >
                <Download className="w-5 h-5 mr-2" strokeWidth={1.5} />
                Download PDF
              </Button>

              <div className="flex items-start space-x-2 p-3 bg-[#E0BFB8]/20 rounded-sm">
                <Printer className="w-4 h-4 text-[#B76E79] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                <p className="text-xs text-[#36454F]/70 font-['Manrope']">
                  Print on standard letter paper. Include copies of your ID and proof of address for faster processing.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Bureau Addresses - All Visible & Copyable */}
          <Card className="border-[#36454F]/15 bg-white animate-fade-in-up animation-delay-200">
            <CardHeader>
              <CardTitle className="font-['Playfair_Display'] text-xl text-[#36454F] flex items-center">
                <MapPin className="w-5 h-5 mr-2 text-[#B76E79]" strokeWidth={1.5} />
                Mailing Addresses
              </CardTitle>
              <CardDescription className="font-['Manrope'] text-[#36454F]/65">
                Send via certified mail with return receipt requested
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {Object.entries(BUREAU_INFO).map(([key, info]) => {
                const fullAddress = `${info.name}\n${info.address}\n${info.city}, ${info.state} ${info.zip}`;
                const isActive = key === dispute?.bureau?.toLowerCase();
                return (
                  <div
                    key={key}
                    className={`p-4 rounded-sm border-l-4 ${
                      isActive ? 'bg-[#B76E79]/5 border-l-[#B76E79]' : 'bg-[#F5F5F5] border-l-[#36454F]/20'
                    }`}
                    data-testid={`bureau-address-${key}`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-['Manrope'] font-semibold text-[#36454F] text-sm">
                          {info.name}
                          {isActive && <span className="ml-2 text-xs text-[#B76E79]">(your letter)</span>}
                        </p>
                        <p className="font-['Manrope'] text-[#36454F]/70 text-sm mt-1">{info.address}</p>
                        <p className="font-['Manrope'] text-[#36454F]/70 text-sm">{info.city}, {info.state} {info.zip}</p>
                      </div>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(fullAddress);
                          toast.success(`${info.name} address copied!`);
                        }}
                        className="p-2 hover:bg-[#36454F]/5 rounded-sm transition-colors flex-shrink-0"
                        title="Copy address"
                        data-testid={`copy-address-${key}`}
                      >
                        <Copy className="w-4 h-4 text-[#36454F]/40" strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>

        {/* Mark as Mailed Section */}
        <Card className="mt-8 border-[#36454F]/15 bg-white animate-fade-in-up animation-delay-300">
          <CardContent className="p-8">
            {!mailed ? (
              <div className="text-center">
                <Clock className="w-12 h-12 text-[#B76E79] mx-auto mb-4" strokeWidth={1.5} />
                <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#36454F] mb-2">
                  Ready to Mail?
                </h2>
                <p className="text-[#36454F]/65 font-['Manrope'] mb-6 max-w-md mx-auto">
                  Once you've mailed your letter, click below to start the 35-day response tracking timer.
                </p>
                <Button
                  onClick={handleMarkMailed}
                  disabled={marking}
                  className="h-14 px-8 bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] font-medium text-lg rounded-sm transition-all duration-300"
                  data-testid="mark-mailed-btn"
                >
                  {marking ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </span>
                  ) : (
                    <span className="flex items-center">
                      <Mail className="w-6 h-6 mr-2" strokeWidth={1.5} />
                      I've Mailed This Letter
                    </span>
                  )}
                </Button>
              </div>
            ) : (
              <div className="text-center">
                <CheckCircle className="w-16 h-16 text-[#4CAF50] mx-auto mb-4" strokeWidth={1.5} />
                <h2 className="font-['Playfair_Display'] text-2xl font-semibold text-[#36454F] mb-2">
                  Tracking Started!
                </h2>
                <p className="text-[#36454F]/65 font-['Manrope'] mb-6">
                  The bureau must respond within 35 days. We'll help you track the deadline.
                </p>
                <div className="inline-flex items-center px-6 py-3 bg-[#4CAF50]/10 border border-[#4CAF50]/30 rounded-sm">
                  <Clock className="w-5 h-5 text-[#4CAF50] mr-3" strokeWidth={1.5} />
                  <span className="font-['Manrope'] font-semibold text-[#36454F]">
                    {mailedData?.days_remaining || 35} days remaining
                  </span>
                </div>
                <div className="mt-8">
                  <Link to="/dashboard">
                    <Button
                      className="bg-[#36454F] hover:bg-[#36454F]/90 text-white font-['Manrope'] rounded-sm"
                      data-testid="go-to-dashboard-btn"
                    >
                      Go to Dashboard
                      <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Cards */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-white border border-[#36454F]/10 rounded-sm">
            <h4 className="font-['Manrope'] font-semibold text-[#36454F] mb-1 text-sm">
              Certified Mail
            </h4>
            <p className="text-xs text-[#36454F]/50 font-['Manrope']">
              Send via USPS Certified Mail for proof of delivery
            </p>
          </div>
          <div className="p-4 bg-white border border-[#36454F]/10 rounded-sm">
            <h4 className="font-['Manrope'] font-semibold text-[#36454F] mb-1 text-sm">
              Keep Copies
            </h4>
            <p className="text-xs text-[#36454F]/50 font-['Manrope']">
              Make copies of everything you send for your records
            </p>
          </div>
          <div className="p-4 bg-white border border-[#36454F]/10 rounded-sm">
            <h4 className="font-['Manrope'] font-semibold text-[#36454F] mb-1 text-sm">
              35-Day Rule
            </h4>
            <p className="text-xs text-[#36454F]/50 font-['Manrope']">
              Bureaus must investigate within 30-35 days by law
            </p>
          </div>
        </div>
      </main>
      <AppFooter />
    </div>
  );
}
